/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../base.component';
import { ChangeDetectorRef, Component, ElementRef, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { ContentInjectionService, ModalService } from '../../services';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-content-overlay',
    templateUrl: './contentOverlay.component.html',
    styleUrls: ['./contentOverlay.component.scss'],
    animations: [
        trigger('openClose', [
            transition('void => true', [ // using status here for transition
                style({ opacity: 0 }),
                animate(250, style({ opacity: 1 }))
            ]),
            transition('true => void', [
                animate(250, style({ opacity: 0 }))
            ])
        ])
    ]
})

export class ContentOverlayComponent extends BaseComponent {
    /**
     * Action dots container reference.
     */
    @ViewChild('contentContainer')
    public contentContainer: ElementRef;

    /**
     * Action dots content template.
     */
    public content: TemplateRef<any>;

    /**
     * Store the entire content object passed from calling component for re-use.
     */
    public overlayContent: any;

    /**
     * Content open state.
     */
    public isOpen: boolean = false;

    /**
     * Store the container height.
     */
    public height: number;

    /**
     * Action dots container position data.
     */
    public top: string | number;
    public bottom: string | number;
    public left: string | number;
    public right: string | number;

    /**
     * Directional state of action dots container.
     */
    public openUpwards: boolean = false;

    /**
     * is the content in the wizard.
     */
    public isEmbedded: boolean = false;

    /**
     * Store dropdown width.
     */
    public dropdownWidth: string = '250px';

    /**
     * Store width of content container.
     */
    public contentWidth: number;

    /**
     * Store the dropdown id.
     */
    public contentId: string;

    /**
     * Store the background colour for the overlay container.
     */
    public backgroundColour: string;

    /**
     * Tooltip caret background colour.
     */
    public tooltipCaretBackgroundColor: string;

    /**
     * Enable overlay flag.
     */
    public overlay: boolean = true;

    /**
     * Store caret position.
     */
    public caretPosition: string;

    /**
     * Show caret flag.
     */
    public showCaret: boolean = true;

    /**
     * Component should fade flag.
     */
    public fade: boolean = false;

    /**
     * Force content to the right of the anchor.
     */
    public forceRight: boolean = false;

    /**
     * Store the offset position for the container.
     * @private
     */
    private contentOffset: any;

    /**
     * Global variables to handle drag in search bar.
     */
    public mousedownTargetId: any;
    public mouseupTargetId: any;
    public mouseDragged: boolean = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private contentInjectionService: ContentInjectionService,
        private modalService: ModalService
    ) {
        super();
        this.setupServiceSubscribers();
        this.setupModalSubscribers();
    }

    /**
     * Mouseup host listener event
     * @param targetElement
     */
    @HostListener('document:mouseup', ['$event.target'])
    public onClick(targetElement: HTMLElement): void {
        this.mouseupTargetId = targetElement.id;
    }

    /**
     * Set mousedown target id.
     * @param targetElement
     */
    @HostListener('document:mousedown', ['$event.target'])
    public handleMousedown(targetElement: HTMLElement): void {
        this.mousedownTargetId = targetElement.id;
    }

    /**
     * Set mousedown target id.
     * @param event
     */
    @HostListener('document:keydown', ['$event'])
    public onKeyPress(event: KeyboardEvent): void {
        const elClassList = Array.from(document.activeElement.classList);
        const tooltipFocused = elClassList.includes('help');
        if (tooltipFocused && event.code === 'Space') {
            event.preventDefault();
        }
        if (!tooltipFocused) {
            return;
        } else {
            this.handleKeyPress(event.code);
        }
    }

    /**
     * Handle click of overlay container.
     */
    public overlayClicked($event: any): void {
        if (!this.searchClicked($event)) {
            this.handleSearchDrag();

            if (this.mouseDragged) {
                this.contentInjectionService.closeEmitter.next(false);
                this.mouseDragged = false;
            } else {
                this.contentInjectionService.closeEmitter.next(true);
            }
        }
    }

    /**
     * Hold content on hover.
     * @param mouseover
     */
    public holdHover(mouseover: boolean): void {
        if (this.contentId !== 'tooltip') {
            return;
        }

        this.contentInjectionService.contentHovered = mouseover;
        if (!this.contentInjectionService.contentHovered) {
            const fadeTimeout = setTimeout(() => {
                this.isOpen = false;
                clearTimeout(fadeTimeout);
            }, 1000);
        }
    }

    /**
     * Handle key press.
     * @param key
     * @private
     */
    private handleKeyPress(key: string): void {
        switch (key) {
            case 'Escape':
                this.contentInjectionService.closeEmitter.next(true);
                break;
            case 'Space':
                this.contentInjectionService.openEmitter.next(this.overlayContent);
                break;
        }
    }

    /**
     * Detect search bar click.
     * @param $event
     * @private
     */
    private searchClicked($event: any): boolean {
        const classArray: string[] = Array.from($event.target.classList);
        const classes = ['dropdown-search', 'clear-search', 'clear-search-img'];

        return classArray.some(item => classes.includes(item));
    }

    /**
     * Handle drag in search bar.
     * @private
     */
    private handleSearchDrag(): void {
        if (this.mousedownTargetId === 'dropdown-search'
            && (!this.mouseupTargetId || this.mouseupTargetId !== 'dropdown-search')) {
            this.mouseDragged = true;
            this.mouseupTargetId = null;
            this.mousedownTargetId = null;
        }
    }

    /**
     * Subscribe to action dots emitters.
     * @private
     */
    private setupServiceSubscribers(): void {
        this.contentInjectionService.openEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (content: any): void => {
                    this.overlayContent = content;
                    if (this.isOpen) {
                        this.isOpen = false;
                    }
                    this.overlay = content.overlay;
                    this.fade = content.fade || false;
                    this.isOpen = content.open;
                    this.content = content.template;
                    this.contentOffset = content.offset;
                    this.forceRight = content.forceRight || false;
                    this.removeWidthFromOffsetInput();
                    this.isEmbedded = content.isEmbedded;
                    this.contentId = content.id;
                    this.cdr.detectChanges();
                    this.setContentWidth(content.containerWidth);
                    this.setOverlayContentHeight(content);
                    this.setContentOpenDirection(content.event);
                    this.setContainerCoordinates(content);
                    this.setCaretPosition(content);
                    this.setCaretColor(content);
                    this.setBackgroundColour(content);
                }
            });

        this.contentInjectionService.closeEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (closed: boolean): void => {
                    if (closed) {
                        this.isOpen = !closed;
                        this.contentInjectionService.contentHovered = !closed;
                    } else {
                        this.isOpen = true;
                        this.contentInjectionService.contentHovered = true;
                    }
                }
            });
    }

    /**
     * Set up modal subscriber.
     * @private
     */
    private setupModalSubscribers(): void {
        this.modalService.openParentModalEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.contentInjectionService.closeEmitter.next(true);
                }
            });
    }

    /**
     * Set the content width;
     * @param width
     * @private
     */
    private setContentWidth(width: string | number): void {
        this.contentWidth = typeof width === 'string' ? this.convertPixelsToNumber(width) : width;
    }

    /**
     * Set overlay container background colour if provided.
     * @param content
     * @private
     */
    private setBackgroundColour(content: any): void {
        if (content.extras && content.extras['background']) {
            this.backgroundColour = content.extras['background'];
        }
    }

    /**
     * Set the overlay content height.
     * @param content
     * @private
     */
    private setOverlayContentHeight(content: any): void {
        this.height = this.contentContainer.nativeElement.querySelector(`#${content.overlaySelector}`).offsetHeight;
    }

    /**
     * Convert pixel position to number if string is passed in.
     * @param position
     * @private
     */
    private convertPixelsToNumber(position: string | number): number {
        if (typeof position === 'string') {
            const split = position.split('px');

            return parseInt(split[0], 10);
        } else {
            return position;
        }
    }

    /**
     * Set coordinates of the content overlay.
     * @param content
     * @private
     */
    private setContainerCoordinates(content: any): void {
        this.setTopAndBottomPositions(content);
        this.setLeftAndRightPositions(content);

        if (this.isHTMLDropdownOpen()) {
            this.setLeftPositionForHTMLDropContainer(content);
        }
    }

    /**
     * Set the left position for the HTML content.
     * @param content
     * @private
     */
    private setLeftPositionForHTMLDropContainer(content: any): void {
        this.left = (content.left - this.contentWidth + content.anchorWidth + this.applyOffsetLeft()) + 'px';
    }

    /**
     * Is the html drop container open.
     * @private
     */
    private isHTMLDropdownOpen(): boolean {
        return this.contentContainer.nativeElement.querySelector('.external-drop-container');
    }

    /**
     * Set top and bottom positions for the overlay container.
     * @param content
     * @private
     */
    private setTopAndBottomPositions(content: any): void {
        this.bottom = this.openUpwards ? ((window.innerHeight - content.bottom) + 'px') : 0;
        this.top = this.openUpwards ? 'unset' : (content.top + 'px');
    }

    /**
     * Set left and right positions for the overlay container.
     * @param content
     * @private
     */
    private setLeftAndRightPositions(content: any): void {
        this.right = content.right ? (content.right + 'px') : 'unset';
        this.left = content.left ? (content.left + 'px') : 'unset';
    }

    /**
     * Set the caret position.
     * @private
     */
    private setCaretPosition(content: any): void {
        this.showCaret = content.showCaret;
        const position: 'left' | 'right' = this.forceRight ? 'left' : 'right';
        this.caretPosition = this.openUpwards ? `bottom-${position}` : `top-${position}`;
    }

    /**
     * Set caret colour.
     * @param content
     * @private
     */
    private setCaretColor(content: any): void {
        this.tooltipCaretBackgroundColor = `transparent transparent ${content.backgroundColor} transparent`;
    }

    /**
     * Remove width key from offset input if provided.
     * @private
     */
    private removeWidthFromOffsetInput(): void {
        if (this.contentOffset?.width) {
            delete this.contentOffset['width'];
        }
    }

    /**
     * Apply offset left position if supplied.
     * @private
     */
    private applyOffsetLeft(): number {
        return this.contentOffset?.left ? this.convertPixelsToNumber(this.contentOffset['left']) : 0;
    }

    /**
     * Set the open direction e.g up/down of the dropdown/action dots content.
     * @param $event
     * @private
     */
    private setContentOpenDirection($event: any): void {
        // Variables declared for better readability.
        const verticalPositionOfClick = $event.y;
        const componentFooterHeight = (this.contentId || this.isEmbedded) ? 96 : 0;

        this.openUpwards = (verticalPositionOfClick + this.height + componentFooterHeight) > window.innerHeight;
    }
}
